import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "../../../shadcn/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../../shadcn/ui/dialog";
import { Input } from "../../../shadcn/ui/input";
import { Textarea } from "../../../shadcn/ui/textarea";
import { Button } from "../../../shadcn/ui/button";
import { Badge } from "../../../shadcn/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../shadcn/ui/select";
import { Label } from "../../../shadcn/ui/label";
import { Switch } from "../../../shadcn/ui/switch";
import {
  Building2,
  Layers,
  Users,
  Search,
  Filter,
  Loader2,
  Calendar,
  MapPin,
  CheckCircle,
  CreditCard,
} from "lucide-react";
import HPageContainer from "../../../hcomponents/HPageContainer";
import HHeader from "../../../hcomponents/HHeader";
import HSimpleFloorPlan from "../../../hcomponents/HSimpleFloorPlan";
import ViewToggle from "../host-spaces/components/ViewToggle";
import GridView from "../host-spaces/components/GridView";
import { buildingAddressService } from "../../../services/buildingAddressService";
import { spaceManagementService } from "../../../services/host-apiServices/spaceManagementService";
import { showApiMessage } from "../../../utils/messageHandler";
import toastUtils from "../../../utils/toast";
import logger from "../../../utils/logger";
import * as localService from "../../../services/localService";
import { convertToUTC, getCurrentTimezoneOffset } from "../../../utils/timezoneUtils";
import { useSubscription } from "../../../hooks/useSubscription";
import { initiateBookingPayment } from "../../../utils/paymentFlowHelper";
import { generateLocationString, generateSpaceName } from "../../../utils/spaceNamingUtils";

const HostCustomerFloorPlan = () => {
  // Core state
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [selectedFloor, setSelectedFloor] = useState(null);

  // Check subscription status for booking endpoint selection
  const { hasActiveSubscription } = useSubscription();

  // Data state
  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [floorPlanData, setFloorPlanData] = useState(null);

  // UI state
  const [showAvailableOnly, setShowAvailableOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("floorplan"); // "floorplan" or "list"

  // Loading states
  const [loadingBuildings, setLoadingBuildings] = useState(true);
  const [loadingFloors, setLoadingFloors] = useState(false);
  const [loadingSpaces, setLoadingSpaces] = useState(false);

  // Booking dialog state
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [bookingSpace, setBookingSpace] = useState(null);
  const [bookingData, setBookingData] = useState({
    startTime: "",
    endTime: "",
    notes: "",
    emails: [""],
  });
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingError, setBookingError] = useState(false);

  // Load buildings on component mount
  useEffect(() => {
    fetchBuildings();
  }, []);

  // Load floors when building changes
  useEffect(() => {
    if (selectedBuilding) {
      fetchFloors(selectedBuilding.id);
    } else {
      setFloors([]);
      setSpaces([]);
    }
  }, [selectedBuilding]);

  const fetchBuildings = async () => {
    try {
      setLoadingBuildings(true);
      const response = await buildingAddressService.getAccessibleBuildingAddresses({
        pageNumber: 0,
        pageSize: 100,
        sort: { sorted: true },
        hasSpaces: true,
      });

      if (response.status === 200 && response.data) {
        const buildingsData = response.data.content || response.data.data || [];
        setBuildings(buildingsData);

        // Auto-select first building if available
        if (buildingsData.length > 0) {
          setSelectedBuilding(buildingsData[0]);
        }
      } else {
        showApiMessage(response, "load buildings");
      }
    } catch (error) {
      logger.error("Error fetching buildings:", error);
      showApiMessage(error, "load buildings");
    } finally {
      setLoadingBuildings(false);
    }
  };

  const fetchFloors = async buildingId => {
    try {
      setLoadingFloors(true);
      const response = await buildingAddressService.getBuildingAddressById(buildingId);

      if (response.status === 200 && response.data) {
        const floorsData = response.data.floors || [];
        setFloors(floorsData);

        // Auto-select first floor if available
        if (floorsData.length > 0) {
          setSelectedFloor(floorsData[0]);
        }
      } else {
        showApiMessage(response, "load floors");
      }
    } catch (error) {
      logger.error("Error fetching floors:", error);
      showApiMessage(error, "load floors");
    } finally {
      setLoadingFloors(false);
    }
  };

  const fetchSpaces = useCallback(async () => {
    if (!selectedBuilding || !selectedFloor) return;

    try {
      setLoadingSpaces(true);

      // Get the floor data to access floor plan
      if (selectedFloor && selectedFloor.floorPlan && selectedFloor.floorPlan.layout) {
        setFloorPlanData(selectedFloor.floorPlan);
      } else {
        setFloorPlanData(null);
      }

      // Fetch spaces for the selected building (only published spaces)
      const response = await spaceManagementService.searchSpaces({
        buildingAddressId: selectedBuilding.id, // Filter by selected building
        pageNumber: 0,
        pageSize: 100, // Get all spaces for this building
        "sort.sorted": true,
        status: "PUBLISHED", // Only fetch published spaces
      });

      if (response.status === 200) {
        const spacesData = response.data?.content || [];

        // Transform spaces to match our UI needs and GridView component structure
        const transformedSpaces = spacesData.map((space, index) => {
          const firstInstance = space.instances?.[0];
          const buildingAddress = firstInstance?.location?.buildingAddress;
          const currentUser = localService.getUser();
          const hostOrgName = currentUser?.organization?.name || currentUser?.organizationName;

          return {
            // Original API fields
            id: space.id,
            size: space.size,
            minimumDuration: space.minimumDuration,
            maximumDuration: space.maximumDuration,
            currency: space.currency,
            ratePrice: space.ratePrice,
            capacity: space.capacity,
            description: space.description,
            images: space.images || [],
            amenities: space.amenities || [],
            status: space.status,
            spaceType: space.spaceType,
            availableInstancesCount: space.availableInstancesCount,
            availableSlots: space.availableSlots || [],
            instances: space.instances || [],
            addons: space.addons || [],
            bufferTimeInMinutes: space.bufferTimeInMinutes,
            category: space.category,
            subCategory: space.subCategory,
            zone: space.zone,

            // UI compatibility fields for GridView
            name: generateSpaceName(
              { ...space, organizationName: space.organizationName || hostOrgName },
              firstInstance,
              true
            ),
            propertyTitle: generateSpaceName(
              { ...space, organizationName: space.organizationName || hostOrgName },
              firstInstance,
              true
            ),
            image: (() => {
              let imageUrl = null;
              if (space.media?.images && Array.isArray(space.media.images) && space.media.images.length > 0) {
                imageUrl = space.media.images.find(img => img && typeof img === "string" && img.trim() !== "");
              }
              if (!imageUrl && space.images && Array.isArray(space.images) && space.images.length > 0) {
                imageUrl = space.images.find(img => img && typeof img === "string" && img.trim() !== "");
              }
              return imageUrl || "/img/placeholder-space.jpg";
            })(),
            propertyStatus: space.status,
            type: space.spaceType,
            propertyType: space.spaceType,
            typeOfWorkspace: space.spaceType,
            pricing: space.ratePrice ? `${space.currency} ${space.ratePrice}/hour` : "Price not set",
            location: generateLocationString(space, firstInstance),
            locationData: {
              address: buildingAddress
                ? [buildingAddress.streetAddress, buildingAddress.city, buildingAddress.region, buildingAddress.country]
                    .filter(Boolean)
                    .join(", ")
                : firstInstance?.location?.name || "No address",
              city: buildingAddress?.city,
              country: buildingAddress?.country,
              latitude: buildingAddress?.latitude,
              longitude: buildingAddress?.longitude,
            },
            date: firstInstance?.createdAt ? new Date(firstInstance.createdAt).toLocaleDateString() : "N/A",
            createdDate: firstInstance?.createdAt,
            instanceCount: space.availableInstancesCount || space.instances?.length || 0,
            coordinates:
              buildingAddress?.latitude && buildingAddress?.longitude
                ? {
                    latitude: buildingAddress.latitude,
                    longitude: buildingAddress.longitude,
                  }
                : null,
            media: {
              images: space.media?.images?.map(url => ({ url })) || space.images?.map(url => ({ url })) || [],
            },

            // Floor plan specific fields
            floorPlanElement: findFloorPlanElement(space, selectedFloor?.floorPlan?.layout),
            uniqueKey: `${space.id}-${index}`,
          };
        });

        // Remove duplicates based on space ID
        const uniqueSpaces = transformedSpaces.filter(
          (space, index, self) => index === self.findIndex(s => s.id === space.id)
        );

        logger.log("Original spaces count:", spacesData.length);
        logger.log("Unique spaces count:", uniqueSpaces.length);
        logger.log("Duplicate IDs found:", spacesData.length !== uniqueSpaces.length);

        setSpaces(uniqueSpaces);
      } else {
        showApiMessage(response, "load spaces");
        setSpaces([]);
      }
    } catch (error) {
      logger.error("Error fetching spaces:", error);
      showApiMessage(error, "load spaces");
      setSpaces([]);
      setFloorPlanData(null);
    } finally {
      setLoadingSpaces(false);
    }
  }, [selectedBuilding, selectedFloor]);

  // Load spaces when floor changes
  useEffect(() => {
    if (selectedFloor && selectedBuilding) {
      fetchSpaces();
    } else {
      setSpaces([]);
    }
  }, [selectedFloor, selectedBuilding, fetchSpaces]);

  // Helper function to find floor plan element for a space
  const findFloorPlanElement = (space, layout) => {
    if (!layout || !Array.isArray(layout)) return null;

    // Look for ROOM elements that might match this space
    const roomElements = layout.filter(element => element.elementType === "ROOM");

    // For now, return the first available room element
    // In a real implementation, you'd have proper mapping between spaces and floor plan elements
    return roomElements[0] || null;
  };

  const _getSpaceColor = status => {
    switch (status) {
      case "available":
        return "bg-green-400 hover:bg-green-500";
      case "unavailable":
        return "bg-gray-400";
      case "occupied":
        return "bg-red-400";
      case "booked":
        return "bg-orange-400";
      case "maintenance":
        return "bg-yellow-400";
      default:
        return "bg-gray-400";
    }
  };

  const getStatusBadge = status => {
    const colors = {
      available: "bg-green-100 text-green-800",
      unavailable: "bg-gray-100 text-gray-800",
      occupied: "bg-red-100 text-red-800",
      booked: "bg-orange-100 text-orange-800",
      maintenance: "bg-yellow-100 text-yellow-800",
    };
    return (
      <Badge className={colors[status] || "bg-gray-100 text-gray-800"}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const handleSpaceClick = space => {
    logger.log("Space clicked:", space.id, space.name);
    // Open booking dialog instead of just selecting space
    handleBookSpace(space);
  };

  const handleBookSpace = space => {
    setBookingSpace(space);
    setBookingData({
      startTime: "",
      endTime: "",
      notes: "",
      emails: [""],
    });
    setBookingSuccess(false);
    setBookingError(false);
    setShowBookingDialog(true);
  };

  const handleBookingInputChange = (field, value) => {
    setBookingData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const _handleEmailChange = (index, value) => {
    setBookingData(prev => ({
      ...prev,
      emails: prev.emails.map((email, i) => (i === index ? value : email)),
    }));
  };

  const _addEmailField = () => {
    setBookingData(prev => ({
      ...prev,
      emails: [...prev.emails, ""],
    }));
  };

  const _removeEmailField = index => {
    if (bookingData.emails.length > 1) {
      setBookingData(prev => ({
        ...prev,
        emails: prev.emails.filter((_, i) => i !== index),
      }));
    }
  };

  const handleCreateBooking = async (usePendingPayment = false) => {
    if (!bookingSpace || !bookingData.startTime || !bookingData.endTime) {
      showApiMessage(
        {
          response: {
            status: 400,
            data: { message: "Please fill in all required fields" },
          },
        },
        "validate booking"
      );
      return;
    }

    try {
      setBookingLoading(true);

      // Get the first instance for booking
      const firstInstance = bookingSpace.instances?.[0];
      if (!firstInstance) {
        throw new Error("No space instance available for booking");
      }

      const bookingPayload = {
        userId: 1, // TODO: Get from user context
        workspaceId: firstInstance.id,
        startTime: convertToUTC(new Date(bookingData.startTime)),
        endTime: convertToUTC(new Date(bookingData.endTime)),
        timezone: getCurrentTimezoneOffset(),
        notes: bookingData.notes || undefined,
        // Convert to UTC - timezone handled on frontend
      };

      logger.log("Creating booking with payload:", bookingPayload);

      // Decide endpoint based on parameter or subscription status
      const response = usePendingPayment
        ? await spaceManagementService.createBookingPendingPayment(bookingPayload)
        : await spaceManagementService.createBooking(bookingPayload);

      if (response.status === 200 || response.status === 201) {
        setBookingSuccess(true);
        setBookingError(false);

        // If using pending payment, initiate payment flow
        if (usePendingPayment) {
          try {
            const booking = response.data?.data || response.data;
            logger.log("Initiating payment for booking:", booking.id);
            const paymentResult = await initiateBookingPayment(booking, bookingSpace);

            if (paymentResult?.success) {
              toastUtils.success("Payment initiated", {
                description: "Complete payment in the new tab to confirm your booking.",
              });
            }
          } catch (paymentError) {
            logger.error("Payment initiation failed:", paymentError);
            toastUtils.warning("Booking created", {
              description: "Please complete payment from your bookings page.",
            });
          }
        } else {
          showApiMessage(response, "create booking");
        }

        // Close dialog after 2 seconds
        setTimeout(() => {
          setShowBookingDialog(false);
          setBookingSuccess(false);
        }, 2000);
      } else {
        showApiMessage(response, "create booking");
      }
    } catch (error) {
      logger.error("Error creating booking:", error);

      // If regular booking failed and user has subscription, enable pay button
      if (!usePendingPayment && hasActiveSubscription) {
        setBookingError(true);
        toastUtils.error("Booking failed", {
          description: "Try using Pay button to complete your booking.",
        });
      } else {
        showApiMessage(error, "create booking");
      }
    } finally {
      setBookingLoading(false);
    }
  };

  const closeBookingDialog = () => {
    setShowBookingDialog(false);
    setBookingSpace(null);
    setBookingData({
      startTime: "",
      endTime: "",
      notes: "",
      emails: [""],
    });
    setBookingSuccess(false);
  };

  // Filter spaces based on search and availability
  const filteredSpaces = spaces.filter(space => {
    const matchesSearch =
      !searchQuery ||
      space.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      space.description?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesAvailability = !showAvailableOnly || space.status === "available";

    return matchesSearch && matchesAvailability;
  });

  // Get spaces statistics for the selected building (only published spaces)
  const spacesStats = {
    total: spaces.length, // All published spaces in the selected building
    available: spaces.length, // All spaces are available since we only fetch published ones
    unavailable: 0, // No unavailable spaces since we only fetch published ones
  };

  return (
    <HPageContainer>
      <HHeader />

      <div className="overflow-y-auto flex-1 p-3 sm:p-4 md:p-6 lg:p-8">
        <div className="mx-auto space-y-6 max-w-7xl">
          {/* Building and Floor Selection */}
          <Card>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Building Selection */}
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4" />
                    Building
                  </Label>
                  <Select
                    value={selectedBuilding?.id?.toString() || ""}
                    onValueChange={value => {
                      const building = buildings.find(b => b.id.toString() === value);
                      setSelectedBuilding(building);
                      setSelectedFloor(null);
                      setSpaces([]);
                    }}
                    disabled={loadingBuildings}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={loadingBuildings ? "Loading buildings..." : "Select a building"} />
                    </SelectTrigger>
                    <SelectContent>
                      {buildings.map(building => (
                        <SelectItem key={building.id} value={building.id.toString()}>
                          <span className="font-medium">{building.buildingName}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Floor Selection */}
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Layers className="w-4 h-4" />
                    Floor
                  </Label>
                  <Select
                    value={selectedFloor?.floorNumber?.toString() || ""}
                    onValueChange={value => {
                      const floor = floors.find(f => f.floorNumber.toString() === value);
                      setSelectedFloor(floor);
                    }}
                    disabled={loadingFloors || !selectedBuilding}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={loadingFloors ? "Loading floors..." : "Select a floor"} />
                    </SelectTrigger>
                    <SelectContent>
                      {floors.map(floor => (
                        <SelectItem key={floor.id || floor.floorNumber} value={floor.floorNumber.toString()}>
                          {floor.label || `Floor ${floor.floorNumber}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Quick Stats */}
              {selectedBuilding && selectedFloor && (
                <div className="mt-4 p-4 rounded-lg">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-gray-900">{spacesStats.total}</div>
                      <div className="text-sm text-gray-600">Total Listings</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600">{spacesStats.available}</div>
                      <div className="text-sm text-gray-600">Available</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-600">{spacesStats.unavailable}</div>
                      <div className="text-sm text-gray-600">Unavailable</div>
                    </div>
                  </div>
                  {searchQuery && (
                    <div className="mt-2 text-xs text-gray-500 text-center">Showing results for "{searchQuery}"</div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Search and Filters */}
          {selectedBuilding && selectedFloor && (
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row gap-4 items-center">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search spaces..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex items-center space-x-2">
                      <Switch id="available-only" checked={showAvailableOnly} onCheckedChange={setShowAvailableOnly} />
                      <Label htmlFor="available-only">Available only</Label>
                    </div>

                    <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
                      <Filter className="w-4 h-4 mr-2" />
                      Filters
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Main Content Area */}
          {loadingBuildings ? (
            <Card>
              <CardContent className="p-12">
                <div className="flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" />
                  <span>Loading buildings...</span>
                </div>
              </CardContent>
            </Card>
          ) : buildings.length === 0 ? (
            <Card>
              <CardContent className="p-12">
                <div className="text-center">
                  <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Buildings Found</h3>
                  <p className="text-gray-500">You need to create building addresses first.</p>
                </div>
              </CardContent>
            </Card>
          ) : !selectedBuilding ? (
            <Card>
              <CardContent className="p-12">
                <div className="text-center">
                  <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Building</h3>
                  <p className="text-gray-500">Choose a building from the dropdown above to view its floor plan.</p>
                </div>
              </CardContent>
            </Card>
          ) : loadingFloors ? (
            <Card>
              <CardContent className="p-12">
                <div className="flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" />
                  <span>Loading floors...</span>
                </div>
              </CardContent>
            </Card>
          ) : floors.length === 0 ? (
            <Card>
              <CardContent className="p-12">
                <div className="text-center">
                  <Layers className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Floors Found</h3>
                  <p className="text-gray-500 mb-4">This building doesn't have any floors configured.</p>
                </div>
              </CardContent>
            </Card>
          ) : !selectedFloor ? (
            <Card>
              <CardContent className="p-12">
                <div className="text-center">
                  <Layers className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Floor</h3>
                  <p className="text-gray-500">Choose a floor from the dropdown above to view its spaces.</p>
                </div>
              </CardContent>
            </Card>
          ) : loadingSpaces ? (
            <Card>
              <CardContent className="p-12">
                <div className="flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" />
                  <span>Loading spaces...</span>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Floor Plan Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {selectedBuilding.buildingName} - {selectedFloor.label || `Floor ${selectedFloor.floorNumber}`}
                  </h2>
                  <p className="text-gray-600">
                    {filteredSpaces.length} of {spaces.length} spaces
                    {searchQuery && ` matching "${searchQuery}"`}
                  </p>
                </div>

                {/* Legend */}
                <div className="flex gap-4 items-center text-sm">
                  <div className="flex gap-1 items-center">
                    <div className="w-3 h-3 bg-green-400 rounded"></div>
                    <span>Available</span>
                  </div>
                  <div className="flex gap-1 items-center">
                    <div className="w-3 h-3 bg-gray-400 rounded"></div>
                    <span>Unavailable</span>
                  </div>
                </div>
              </div>

              {/* View Toggle */}
              <div className="flex justify-end mb-4">
                <ViewToggle activeView={viewMode} onViewChange={setViewMode} availableViews={["floorplan", "list"]} />
              </div>

              {/* Floor Plan or List View */}
              {viewMode === "floorplan" ? (
                <Card>
                  <CardContent className="p-6">
                    <div className="h-96">
                      {floorPlanData && floorPlanData.layout && floorPlanData.layout.length > 0 ? (
                        <HSimpleFloorPlan
                          initialLayout={floorPlanData.layout}
                          readOnly={true}
                          className="w-full h-full"
                          useIcons={true}
                          useColors={false}
                        />
                      ) : (
                        <div className="relative bg-gray-100 rounded-lg h-full border-2 border-dashed border-gray-300 flex items-center justify-center">
                          <div className="text-center">
                            <Layers className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No Floor Plan</h3>
                            <p className="text-gray-500">This floor doesn't have a floor plan configured yet.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div>
                  {filteredSpaces.length === 0 ? (
                    <Card>
                      <CardContent className="p-6">
                        <div className="text-center py-8">
                          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No Spaces Found</h3>
                          <p className="text-gray-500">
                            {searchQuery
                              ? "No spaces match your search criteria."
                              : "No spaces are available on this floor."}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    <GridView spaces={filteredSpaces} onSelectSpace={handleSpaceClick} onBookSpace={handleBookSpace} />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Booking Dialog */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Book Space
            </DialogTitle>
            <DialogDescription>
              {bookingSpace && `Book "${bookingSpace.name}" - ${bookingSpace.capacity} people capacity`}
            </DialogDescription>
          </DialogHeader>

          {bookingSuccess ? (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Booking Successful!</h3>
              <p className="text-gray-600">Your space has been booked successfully.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Space Details */}
              {bookingSpace && (
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">{bookingSpace.name}</h3>
                        <p className="text-sm text-gray-600">{bookingSpace.description}</p>
                        {bookingSpace.location && (
                          <div className="flex items-center gap-1 mt-1 text-sm text-gray-500">
                            <MapPin className="w-4 h-4" />
                            <span>{bookingSpace.location}</span>
                          </div>
                        )}
                      </div>
                      {getStatusBadge(bookingSpace.status)}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div className="text-center p-2 bg-gray-50 rounded">
                        <div className="font-medium text-gray-900">{bookingSpace.capacity}</div>
                        <div className="text-gray-600">Capacity</div>
                      </div>
                      <div className="text-center p-2 bg-gray-50 rounded">
                        <div className="font-medium text-gray-900">{bookingSpace.spaceType}</div>
                        <div className="text-gray-600">Type</div>
                      </div>
                      <div className="text-center p-2 bg-gray-50 rounded">
                        <div className="font-medium text-green-600">{bookingSpace.pricing}</div>
                        <div className="text-gray-600">Price</div>
                      </div>
                      <div className="text-center p-2 bg-gray-50 rounded">
                        <div className="font-medium text-gray-900">{bookingSpace.instanceCount}</div>
                        <div className="text-gray-600">Instances</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Booking Form */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="startTime">Start Time *</Label>
                    <Input
                      id="startTime"
                      type="datetime-local"
                      value={bookingData.startTime}
                      onChange={e => handleBookingInputChange("startTime", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="endTime">End Time *</Label>
                    <Input
                      id="endTime"
                      type="datetime-local"
                      value={bookingData.endTime}
                      onChange={e => handleBookingInputChange("endTime", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    value={bookingData.notes}
                    onChange={e => handleBookingInputChange("notes", e.target.value)}
                    placeholder="Add any special requirements or notes..."
                    className="mt-1"
                    rows={3}
                  />
                </div>

                {/* Available Time Slots */}
                {bookingSpace?.availableSlots && bookingSpace.availableSlots.length > 0 && (
                  <div>
                    <Label>Available Time Slots</Label>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                      {bookingSpace.availableSlots.slice(0, 6).map((slot, index) => (
                        <div key={index} className="p-2 bg-blue-50 rounded text-sm">
                          <span className="font-medium text-blue-900">{slot.day}</span>: {slot.startTime} -{" "}
                          {slot.endTime}
                        </div>
                      ))}
                      {bookingSpace.availableSlots.length > 6 && (
                        <div className="p-2 bg-gray-100 rounded text-sm text-gray-600">
                          +{bookingSpace.availableSlots.length - 6} more slots
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Amenities */}
                {bookingSpace?.amenities && bookingSpace.amenities.length > 0 && (
                  <div>
                    <Label>Available Amenities</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {bookingSpace.amenities.map((amenity, index) => (
                        <span key={index} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-md">
                          {amenity.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 justify-end pt-4 border-t">
                <Button variant="outline" onClick={closeBookingDialog} disabled={bookingLoading}>
                  Cancel
                </Button>

                {/* Show Book button if user has active subscription */}
                {hasActiveSubscription && (
                  <Button
                    onClick={() => handleCreateBooking(false)}
                    disabled={bookingLoading || !bookingData.startTime || !bookingData.endTime}
                    className="min-w-[120px]"
                  >
                    {bookingLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Booking...
                      </>
                    ) : (
                      <>
                        <Calendar className="w-4 h-4 mr-2" />
                        Book Space
                      </>
                    )}
                  </Button>
                )}

                {/* Show Pay button if: (1) no subscription OR (2) booking failed */}
                {(!hasActiveSubscription || bookingError) && (
                  <Button
                    onClick={() => handleCreateBooking(true)}
                    disabled={bookingLoading || !bookingData.startTime || !bookingData.endTime}
                    className="min-w-[120px] bg-green-600 hover:bg-green-700"
                  >
                    {bookingLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Pay
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </HPageContainer>
  );
};

export default HostCustomerFloorPlan;
